import {
  assetsHybridSearchPreseededRetrieve,
  assetsUserHomepageQueriesUpdateForUserPartialUpdate,
  assetsHybridSearchRefreshCreate
} from '@goldcast/api/content';
import { Clip } from '@goldcast/player-config/src/types/clip-types';
import createStore from './store';
import { currentUser } from '@/stores/user';
import { TopicGroup } from '@/Pages/HomePageNew/ClipRecommendation/types';

interface ClipRecommendationsResponse {
  organization: string;
  topics: TopicGroup[];
}

export const clipRecommendationsStore = createStore<{ [index: number]: TopicGroup } | null>(null);
export const clipRecommendationsLoadingStore = createStore<boolean>(false);

export function setClipRecommendations(recommendations: ClipRecommendationsResponse | null) {
  if (!recommendations) {
    clipRecommendationsStore.set(() => null);
    return;
  }

  const indexedTopics: { [index: number]: TopicGroup } = {};

  // Ensure topics is an array
  if (!Array.isArray(recommendations.topics)) {
    clipRecommendationsStore.set(() => null);
    return;
  }

  recommendations.topics.forEach((topicGroup, index) => {
    // Skip invalid topic groups
    if (!topicGroup || typeof topicGroup !== 'object') {
      return;
    }

    const savedSearch = topicGroup.results?.[0]?.saved_search;
    const fallback = topicGroup.topic || 'Untitled Topic';

    indexedTopics[index] = {
      topic: topicGroup.topic || fallback,
      results: Array.isArray(topicGroup.results) ? topicGroup.results.filter((clip: Clip) => clip && clip.id) : [],
      searchTerm: savedSearch?.search_term || fallback,
      title: savedSearch?.title || fallback,
      isLoading: topicGroup.status === 'pending' ? true : (topicGroup.isLoading ?? false),
      status: topicGroup.status,
      savedSearchId: savedSearch?.id
    };
  });

  clipRecommendationsStore.set(() => indexedTopics);
}

export function setClipRecommendationsLoading(loading: boolean) {
  clipRecommendationsLoadingStore.set(() => loading);
}

export function setTopicGroupLoading(topicIndex: number, loading: boolean) {
  const currentRecommendations = clipRecommendationsStore.getSnapshot();
  if (currentRecommendations && currentRecommendations[topicIndex]) {
    const updatedRecommendations = {
      ...currentRecommendations,
      [topicIndex]: {
        ...currentRecommendations[topicIndex],
        isLoading: loading
      }
    };
    clipRecommendationsStore.set(() => updatedRecommendations);
  }
}

export async function loadClipRecommendations(organization: string, showLoading: boolean = true) {
  if (!organization) {
    return;
  }

  if (showLoading) {
    setClipRecommendationsLoading(true);
  }
  try {
    const clipResponse = await assetsHybridSearchPreseededRetrieve({ queryParams: { limit: 6, organization } });

    if (!clipResponse) {
      setClipRecommendations(null);
      return;
    }

    const recommendations = clipResponse as unknown as ClipRecommendationsResponse;

    // Validate response structure
    if (!recommendations || typeof recommendations !== 'object') {
      setClipRecommendations(null);
      return;
    }

    setClipRecommendations(recommendations);
  } catch (error) {
    window.logger?.error('Error while loading clip recommendations', error);
    setClipRecommendations(null);
  } finally {
    setClipRecommendationsLoading(false);
  }
}

export function updateClipRecommendations(clipId: string, response: Clip) {
  if (!clipId || !response || !response.id) {
    return;
  }

  const clipRecommendations = clipRecommendationsStore.getSnapshot();
  if (clipRecommendations) {
    const updatedRecommendations: { [index: number]: TopicGroup } = {};

    Object.entries(clipRecommendations).forEach(([indexStr, topicGroup]) => {
      const index = parseInt(indexStr);
      if (!topicGroup || !Array.isArray(topicGroup.results)) {
        return;
      }

      updatedRecommendations[index] = {
        ...topicGroup,
        results: topicGroup.results
          .map((clip: Clip) => (clip?.id === clipId ? response : clip))
          .filter((clip: Clip) => clip && clip.id)
      };
    });

    clipRecommendationsStore.set(() => updatedRecommendations);
  }
}

export function updateClipTopic(topicIndex: number, topic: string, searchTerm: string, workspaceId?: string) {
  if (!topic || !searchTerm) {
    return;
  }

  const clipRecommendations = clipRecommendationsStore.getSnapshot();
  if (!clipRecommendations || !clipRecommendations[topicIndex]) {
    return;
  }

  const currentTopicGroup = clipRecommendations[topicIndex];
  if (!currentTopicGroup) {
    return;
  }

  const updatedRecommendations = {
    ...clipRecommendations,
    [topicIndex]: {
      ...currentTopicGroup,
      title: topic,
      searchTerm: searchTerm,
      isLoading: true
    }
  };
  clipRecommendationsStore.set(() => updatedRecommendations);

  const topics = Object.values(clipRecommendations)
    .filter(tg => tg && tg.searchTerm)
    .map(topicGroup => topicGroup.searchTerm);

  if (topics.length > topicIndex) {
    topics[topicIndex] = searchTerm;
  } else {
    topics.push(searchTerm);
  }

  const userId = currentUser.getSnapshot()?.id;
  const organization = currentUser.getSnapshot()?.organization;

  if (!userId || !organization) {
    setTopicGroupLoading(topicIndex, false);
    return;
  }

  assetsUserHomepageQueriesUpdateForUserPartialUpdate({
    queryParams: {
      user_id: userId,
      workspace_id: workspaceId || ''
    },
    body: {
      query_list: topics
    }
  })
    .then(() => {
      loadClipRecommendations(organization, false);
    })
    .catch(error => {
      window.logger?.error('Error while updating topic', error);
      setTopicGroupLoading(topicIndex, false);
    });
}

export function getTopicsAsArray(): TopicGroup[] {
  const clipRecommendations = clipRecommendationsStore.getSnapshot();
  if (!clipRecommendations) {
    return [];
  }

  return Object.values(clipRecommendations);
}

export function getTopicByIndex(index: number): TopicGroup | null {
  const clipRecommendations = clipRecommendationsStore.getSnapshot();
  return clipRecommendations?.[index] || null;
}

export function isSavedSearchIdAlreadyDone(savedSearchId: string): boolean {
  const clipRecommendations = clipRecommendationsStore.getSnapshot();
  if (!clipRecommendations) {
    return false;
  }
  return Object.values(clipRecommendations).some(
    topicGroup => topicGroup.savedSearchId === savedSearchId && topicGroup.status === 'done'
  );
}

export function handleRefreshTopic(topicIndex: number) {
  const clipRecommendations = clipRecommendationsStore.getSnapshot();
  const organization = currentUser.getSnapshot()?.organization;
  const topicGroup = clipRecommendations?.[topicIndex];
  const savedSearchId = topicGroup?.results?.[0]?.saved_search?.id;

  if (!clipRecommendations || !organization) {
    return;
  }

  if (!topicGroup) {
    return;
  }

  if (!savedSearchId) {
    setTopicGroupLoading(topicIndex, false);
    return;
  }

  setTopicGroupLoading(topicIndex, true);

  assetsHybridSearchRefreshCreate({
    id: savedSearchId,
    body: {
      generation_type: 'CLIP'
    }
  })
    .then(res => {
      if (!res) {
        setTopicGroupLoading(topicIndex, false);
        return;
      }

      const clips = res as unknown as Clip[];
      const validClips = Array.isArray(clips) ? clips.filter((clip: Clip) => clip && clip.id) : [];

      const updatedRecommendations = {
        ...clipRecommendations,
        [topicIndex]: {
          ...topicGroup,
          results: validClips
        }
      };
      clipRecommendationsStore.set(() => updatedRecommendations);
      setTopicGroupLoading(topicIndex, false);
    })
    .catch(error => {
      window.logger?.error('Error while refreshing topic', error);
      setTopicGroupLoading(topicIndex, false);
    });
}
